import { readProductCurrency } from '@/domain/product/currency'
import sanitizeHtml from 'sanitize-html'
import { displayFloatValue, formatPrice } from '@skop-app/lib-domain/utils'
import { format } from 'date-fns'
import { uniqueBy } from 'remeda'

import {
  formatProductDimensions,
  getCustomProductName,
  getTranslation,
  matchKey,
} from '@/utils'
import type { AdaptedProduct, Context } from '@/types'

type FormattedProductItem = {
  name: string
  key: string
  title: string | number | boolean | null
  id?: string
  value?: string | number
  short?: string
  subTitle?: string
  description?: string
}

type FormattedViewerItem = {
  productItems: FormattedProductItem[]
  name: string
  pictures: string[]
  productID: string
  id: string
}

const hasDisplayTitle = (item: FormattedProductItem): boolean =>
  item.title !== null && item.title !== undefined && item.title !== ''

const parseOptionalQuantity = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null

  const trimmed = value.trim().replace(/\s+/g, '').replace(',', '.')
  if (!trimmed.length) return null

  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const getQuantityNumber = (
  source: Pick<AdaptedProduct, 'quantity.number'>,
): number | null =>
  parseOptionalQuantity(
    source?.['quantity.number']?.value ?? source?.['quantity.number']?.label,
  )

export const transformLineBreaks = (text: string): string =>
  eval(
    '`' +
      sanitizeHtml(text)
        .replace(/\r\n/g, "${'\\n'}") // Windows style line endings (CRLF)
        .replace(/\r/g, "${'\\r'}") // Old Mac style line endings (CR)
        .replace(/\n/g, "${'\\n'}") // Unix style line endings (LF)
        .replace(/&amp;/g, "${'&'}") +
      '`',
  )

export const formatItems = ({
  item,
  context,
  locale = 'fr',
  fallbackLocales,
}: {
  item: AdaptedProduct
  context: Context
  locale?: string
  fallbackLocales?: readonly string[]
}): FormattedViewerItem => {
  const startDate =
    item.startDate?.value &&
    format(
      new Date(item.startDate.value),
      locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy',
    )

  const endDate =
    item.endDate?.value &&
    format(
      new Date(item.endDate.value),
      locale === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy',
    )

  const doubleDate =
    startDate && endDate ? `Disponible du ${startDate} au ${endDate}` : ''
  const startDateOnly = startDate ? `Disponible à partir du ${startDate}` : ''
  const endDateOnly = endDate ? `Disponible jusqu'au ${endDate}` : ''

  const availability = doubleDate || startDateOnly || endDateOnly

  const brand =
    (item.brand?.label ?? item.model?.label) &&
    `${item.brand?.label ?? ''}${item.brand?.label && ' '}${item.model?.label ?? ''}`

  // Format quantity
  const quantity = getQuantityNumber(item)
  const quantityUnit = item?.['quantity.unit']?.label ?? ''

  // Handle batchedProducts quantity
  const batchedProductsQuantity =
    item?.batchedProducts
      ?.map((product) => getQuantityNumber(product))
      .filter((value): value is number => value !== null) ?? []
  const totalBatchedQuantity =
    batchedProductsQuantity.length > 0
      ? batchedProductsQuantity.reduce((acc, value) => acc + value, 0)
      : null

  const totalQuantity = totalBatchedQuantity ?? quantity
  const quantityValue =
    totalQuantity === null
      ? null
      : typeof quantityUnit === 'string' && quantityUnit.length > 0
        ? `${totalQuantity} ${quantityUnit.toLowerCase()}`
        : totalQuantity

  // Batched products spots (limit to 10 unique values)
  const spots =
    item?.batchedProducts
      ?.map((product) => product?.spot?.label)
      .filter((spot): spot is string => typeof spot === 'string') ?? []
  const spotsValue = uniqueBy(
    [...(item.spot?.label ? [item.spot.label] : []), ...spots],
    (spot) => spot,
  ).slice(0, 10)

  const hasRef = item.ref?.label && item.ref?.value !== item.name?.label
  const pictures = item.pictures ?? []

  const productItems: FormattedProductItem[] = [
    {
      name: 'Référence',
      key: 'ref',
      title: hasRef && item.ref?.label ? item.ref.label : null,
    },
    {
      name: 'Stock',
      key: 'stock',
      title: quantityValue,
    },
    {
      name: 'Prix de vente',
      key: 'price',
      title: item['price.amount']?.value
        ? formatPrice({
            amount: Number(item['price.amount'].value),
            currency: readProductCurrency(
              item as unknown as Record<string, unknown>,
            ),
          })
        : null,
      short: 'Prix de vente',
    },
    {
      name: 'Prix de revient',
      key: 'price.amount_1',
      title: item['price.amount_1']?.value
        ? formatPrice({
            amount: Number(item['price.amount_1'].value),
            currency: readProductCurrency(
              item as unknown as Record<string, unknown>,
            ),
          })
        : null,
      short: 'Prix de revient',
    },
    {
      name: 'Prix neuf',
      key: 'price.amount_2',
      title: item['price.amount_2']?.value
        ? formatPrice({
            amount: Number(item['price.amount_2'].value),
            currency: readProductCurrency(
              item as unknown as Record<string, unknown>,
            ),
          })
        : null,
      short: 'Prix neuf',
    },
    {
      name: 'Etat',
      key: 'condition',
      title:
        matchKey(
          item.condition?.label ?? '',
          context.propertyOptions?.condition ?? [],
        )?.label ?? null,
    },
    {
      name: 'Statut',
      key: 'status',
      id: item.id || '',
      title:
        item.type !== 'creation'
          ? (context.actionCatalog?.actionOptionByValue[
              item.action ?? 'created'
            ]?.label ??
            context.refDataByKey?.action?.[item.action ?? 'created'] ??
            null)
          : null,
    },
    {
      name: 'Dimensions',
      key: 'dimensions',
      title: formatProductDimensions(item)?.label ?? '',
    },
    {
      name: 'Poids',
      key: 'weight',
      title: item.weight ? `${displayFloatValue(item.weight.value)} kg` : null,
    },
    {
      name: 'Matériaux',
      key: 'materials',
      title: Array.isArray(item.material)
        ? item.material.map((material) => material.label).join(', ')
        : null,
    },
    {
      name: 'Couleur',
      key: 'color',
      title: Array.isArray(item.color)
        ? item.color.map((color) => color.label).join(', ')
        : null,
    },
    {
      name: 'Marque',
      key: 'brand',
      title: brand ?? null,
    },
    {
      name: 'Disponibilité',
      key: 'availability',
      id: item.id || '',
      title: availability,
    },
    {
      name: 'Potentiel de réemploi',
      key: 'reemploymentIndex',
      title: item.reemploymentIndex?.label ?? null,
      value: item.reemploymentIndex?.value,
    },
    {
      name: 'Potentiel de réutilisation',
      key: 'reusabilityIndex',
      title: item.reusabilityIndex?.label ?? null,
      value: item.reusabilityIndex?.value,
    },
    {
      name: 'Potentiel de recyclage',
      key: 'recyclabilityIndex',
      title: item.recyclabilityIndex?.label ?? null,
      value: item.recyclabilityIndex?.value,
    },
    {
      name: 'Potentiel de valorisation',
      key: 'potential',
      title: item.instructionPotential?.label ?? null,
    },
    {
      name: 'Mode de fixation',
      key: 'assembly',
      title: item.instructionAssembly?.label ?? null,
    },
    {
      name: 'Méthode de dépose',
      key: 'removal',
      title: item.instructionRemoval?.label ?? null,
    },
    {
      name: 'Modalité de transport',
      key: 'transport',
      title: item.instructionTransport?.label ?? null,
    },
    {
      name: 'Stockage',
      key: 'storage',
      title: item.instructionStorage?.label ?? null,
    },
    {
      name: 'Conditionnement',
      key: 'conditioning',
      title: item.instructionConditioning?.label ?? null,
    },
    {
      name: 'Risques et précautions',
      key: 'safety',
      title: item.instructionSafety?.label ?? null,
    },
    {
      name: 'Description',
      key: 'description',
      title: getTranslation(item, 'description', locale, fallbackLocales),
    },
    {
      name: 'Lieu',
      key: 'placeID',
      title: item.placeID?.label ?? null,
      ...(spotsValue.length > 0 && { subTitle: spotsValue.join(', ') }),
    },
    {
      name: 'Projet',
      key: 'project',
      title: !item.projectID?.label?.includes(item.placeID?.label ?? '')
        ? (item.projectID?.label ?? null)
        : null,
    },
  ]

  return {
    productItems: productItems.filter(hasDisplayTitle),
    name: getCustomProductName(item, locale, fallbackLocales),
    pictures,
    productID: item.productID || '',
    id: item.id || '',
  }
}
